/**
 * Agent · Questions (phase B). The compliance team answers what people
 * asked through the agent, with the agent's context attached; an answer may
 * be marked standing guidance, which the agent then quotes on similar items
 * for a year.
 */
import React, { useState } from 'react';
import { api, fmtWhen } from './api.js';

function Answer({ q, onDone }) {
  const [text, setText] = useState('');
  const [standing, setStanding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const send = async () => {
    setBusy(true); setMsg(null);
    const d = await api(`/questions/${q.id}/answer`, { method: 'POST', body: { answer: text, standing } });
    setBusy(false);
    if (!d?.success) { setMsg(d?.message || 'Could not answer.'); return; }
    onDone?.();
  };
  return (
    <div className="mt-2">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={4000} placeholder="Your answer. The asker hears at once, here and by email." className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" />
      <div className="mt-2 flex items-center gap-3 flex-wrap">
        <button type="button" onClick={send} disabled={busy || !text.trim()} className="px-4 py-1.5 rounded-full bg-[#E5601F] text-white text-[13px] font-medium disabled:opacity-50">Answer</button>
        <label className="text-[13px] text-slate-700 flex items-center gap-2"><input type="checkbox" checked={standing} onChange={(e) => setStanding(e.target.checked)} /> Standing guidance: the agent quotes this on similar items for one year</label>
        {msg && <span className="text-[13px] text-rose-700">{msg}</span>}
      </div>
    </div>
  );
}

export default function QuestionsTab({ questions, onRefresh }) {
  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState(null);
  const reconfirm = async (q) => {
    const d = await api(`/questions/${q.id}/reconfirm`, { method: 'POST' });
    if (!d?.success) { setNote(d?.message || 'Could not re-confirm.'); return; }
    setNote('Kept for another year.'); onRefresh?.();
  };
  const open = questions.filter((q) => q.status === 'open');
  const answered = questions.filter((q) => q.status !== 'open');
  const Row = ({ q }) => (
    <div className="py-3 border-b border-slate-100 last:border-0">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-800"><span className="font-medium">{q.asker_name || 'A colleague'}</span>{q.object_label ? <span className="text-slate-500"> · {q.object_label}</span> : null}{q.escalated_at ? <span className="ml-2 text-[11px] text-orange-700">escalated to the officer</span> : null}{q.standing ? <span className="ml-2 text-[11px] text-emerald-700">standing guidance</span> : null}</p>
        <span className="text-xs text-slate-400">{fmtWhen(q.created_at)}</span>
      </div>
      <p className="text-sm text-slate-700 mt-1">{q.question}</p>
      {q.context && <details className="mt-1"><summary className="text-xs text-slate-500 cursor-pointer">What the agent had noticed</summary><p className="text-xs text-slate-500 mt-1 whitespace-pre-wrap">{q.context}</p></details>}
      {q.answer && <p className="mt-2 text-sm text-slate-900 border-l-2 border-[#E5601F] pl-3">{q.answer} <span className="text-slate-400">· {q.answered_by_name || 'Compliance'}, {fmtWhen(q.answered_at)}</span></p>}
      {q.standing && q.expires_at && <p className="mt-1 text-xs text-slate-500">Standing until {fmtWhen(q.expires_at)}. <button type="button" onClick={() => reconfirm(q)} className="text-[#E5601F] hover:underline">Keep for another year</button></p>}
      {q.status === 'open' && (openId === q.id ? <Answer q={q} onDone={() => { setOpenId(null); onRefresh?.(); }} /> : <button type="button" onClick={() => setOpenId(q.id)} className="mt-2 text-[12.5px] font-medium text-[#E5601F] hover:underline">Answer</button>)}
    </div>
  );
  return (
    <div className="space-y-6">
      {note && <p className="text-sm text-slate-700">{note}</p>}
      <section className="bg-white rounded-2xl border border-slate-200">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between"><h3 className="text-sm font-semibold text-slate-800">Waiting for an answer</h3><span className="text-xs text-slate-500">{open.length}</span></div>
        <div className="px-5 py-2">{open.length === 0 ? <p className="text-sm text-slate-500 py-2">Nobody is waiting on an answer.</p> : open.map((q) => <Row key={q.id} q={q} />)}</div>
      </section>
      <section className="bg-white rounded-2xl border border-slate-200">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between"><h3 className="text-sm font-semibold text-slate-800">Answered</h3><span className="text-xs text-slate-500">{answered.length}</span></div>
        <div className="px-5 py-2">{answered.length === 0 ? <p className="text-sm text-slate-500 py-2">Nothing answered yet.</p> : answered.map((q) => <Row key={q.id} q={q} />)}</div>
      </section>
    </div>
  );
}
